import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { Grid, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { CrmJobFilterDTO, CrmJobStatusDTO, resetAllMailingLists } from 'wisestep-api';
import { WsButton, SearchBarNew, WsColors, WsAutoComplete } from 'wisestep-base';

import { StyledBox } from '../../../features/clientListingPage/helper/ClientListingHelper';
import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { useCrmJobsAuthz, CrmJobsAccess } from '../../../hooks/useCrmJobsAuthz';
import { useNotificationStore } from '../../../utils/NotificationStore';

interface CrmJobFiltersProps {
  onAdvancedFiltersClick: () => void;
  searchQuery: CrmJobFilterDTO;
  getUpdatedFiltersFromFilters: (query: CrmJobFilterDTO) => void;
  clear: () => void;
  handleDownload?: (type: string) => void;
  fetchCrmJobs: () => void;
  fromClientTab: boolean;
  jobStatusWithIds: CrmJobStatusDTO[];
  jobStatusList: string[];
}

const CrmJobFilters = ({
  onAdvancedFiltersClick,
  searchQuery,
  getUpdatedFiltersFromFilters,
  clear,
  handleDownload,
  fetchCrmJobs,
  fromClientTab,
  jobStatusList,
  jobStatusWithIds,
}: CrmJobFiltersProps) => {
  const methods = useForm({
    defaultValues: {
      query: searchQuery.query || '',
    },
  });
  const { handleSubmit, reset, getValues } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const canDownloadCrmJobs = useCrmJobsAuthz(CrmJobsAccess.DOWNLOAD_CRM_JOBS);

  const handleClear = () => {
    reset();
    clear();
  };

  const handleResetMailingList = async () => {
    setResetLoading(true);
    try {
      await resetAllMailingLists();
      fetchCrmJobs();
      setNotification({ message: 'All mailing lists reset successfully', type: 'success' });
    } catch (error) {
      console.error('Error resetting all mailing lists:', error);
      setNotification({ message: 'Failed to reset mailing lists', type: 'error' });
    } finally {
      setResetLoading(false);
    }
  };

  const [statusValue, setStatusValue] = useState<string[]>([]);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(downloadAnchor);

  const handleCloseDownloadOptions = () => setDownloadAnchor(null);

  const onClickDownload = (type: string) => {
    handleDownload && handleDownload(type);
    handleCloseDownloadOptions();
  };

  const handleOpenDownloadOptions = (event: React.MouseEvent<HTMLElement>) =>
    setDownloadAnchor(event.currentTarget);

  const onStatusSearch = () => {
    const query = getValues('query');
    const statusIds = statusValue
      .flatMap((status) =>
        jobStatusWithIds
          .filter((statusObj) => statusObj.statusName === status)
          .map((statusObj) => statusObj.id)
      )
      .filter((id): id is string => id !== undefined);
    getUpdatedFiltersFromFilters({
      ...searchQuery,
      jobStatuses: statusIds,
      query,
    });
  };

  const onSearch = () => {
    const query = getValues('query');
    getUpdatedFiltersFromFilters({
      ...searchQuery,
      query,
    });
  };

  const onStatusSelection = (statusInput: string[]) => setStatusValue(statusInput);

  return (
    <StyledBox sx={{ border: 'none' }}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSearch)}>
          <Grid container bgcolor={WsColors.White}>
            <Grid item xs={4}>
              <SearchBarNew
                size="small"
                name="query"
                placeholder="Search Clients"
                type="submit"
                onClear={handleClear}
              />
            </Grid>
            <Grid item>
              <SmartFilter
                filterName="Status"
                onClick={onStatusSearch}
                filterValue={getFilterValue('status', methods)}
                onClear={clear}
              >
                <WsAutoComplete
                  key={`status-${statusValue.join(',')}`}
                  onTagsChange={onStatusSelection}
                  placeholder="Eg, Open"
                  initialValue={statusValue}
                  value={statusValue}
                  optionsInStringArray={jobStatusList}
                  multiple={true}
                  freeSolo={true}
                  name="status"
                  size="small"
                />
              </SmartFilter>
            </Grid>
            <Grid item xs>
              <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
                {canDownloadCrmJobs && handleDownload && !fromClientTab && (
                  <Grid item>
                    <WsButton
                      onClick={handleOpenDownloadOptions}
                      startIcon={<FileDownloadOutlinedIcon />}
                      variant="text"
                      size="medium"
                    >
                      Export
                    </WsButton>
                    <Menu
                      anchorEl={downloadAnchor}
                      open={open}
                      onClose={handleCloseDownloadOptions}
                    >
                      <MenuItem onClick={() => onClickDownload('csv')}>Download as CSV</MenuItem>
                      <MenuItem onClick={() => onClickDownload('xls')}>Download as XLS</MenuItem>
                    </Menu>
                  </Grid>
                )}
                <Grid item>
                  <WsButton
                    variant="outlined"
                    size="medium"
                    onClick={onAdvancedFiltersClick}
                    startIcon={<BiFilterAlt />}
                  >
                    Advanced filters
                  </WsButton>
                </Grid>
              </Grid>
            </Grid>
            {!fromClientTab && (
              <Grid container justifyContent="flex-end" alignItems="center" pt={2} spacing={1}>
                <Grid item>
                  <WsButton
                    variant="contained"
                    size="medium"
                    onClick={handleResetMailingList}
                    startIcon={<RestartAltOutlinedIcon />}
                    disabled={resetLoading}
                  >
                    {resetLoading ? 'Resetting...' : 'Reset Mailing List'}
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </StyledBox>
  );
};

export default CrmJobFilters;
